import { Component, Input, OnDestroy, OnInit, Optional } from "@angular/core";
import { AbstractControl, ControlContainer, FormArray, FormGroup, ValidationErrors } from "@angular/forms";
import { Observable, Subject } from "rxjs";

import { DataRowState } from "@tylertech/shared-module";
import { IdValuePair, MailingAddressViewModel } from "@tylertech/tyler-api-types";

import { ContactService } from "../../services";


interface ExpandableMailingAddress extends MailingAddressViewModel
{
    ExpandedDetail?: boolean;
}

@Component({
    selector: "mailing-address-list",
    templateUrl: "./mailingAddressList.tmpl.html",
    standalone: false
})
export class MailingAddressListComponent implements OnInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    private readonly refreshSubject = new Subject<void>();
    readonly mailingAddressForm: FormGroup;

    @Input() readonly usages: ReadonlyArray<IdValuePair>;
    @Input() readonly mailingAddresses: Array<ExpandableMailingAddress>;
    @Input() readonly viewOnly: boolean;

    constructor(
        private readonly $contactService: ContactService,
        @Optional() private readonly controlContainer: ControlContainer | null)
    {
        this.mailingAddressForm = new FormGroup(
            {
                rows: new FormArray([])
            },
            [
                (_control: AbstractControl): ValidationErrors | null =>
                {
                    if (this.mailingAddresses && this.mailingAddresses.filter(x => x.RowState !== DataRowState.Deleted).length)
                    {
                        return null;
                    }

                    return {
                        "atLeastOne": true
                    };
                },
                (_control: AbstractControl): ValidationErrors | null =>
                {
                    if (this.mailingAddresses && this.mailingAddresses.find(x => x.IsPrimary))
                    {
                        return null;
                    }

                    return {
                        "noPrimary": true
                    };
                }
            ]
        );
    }

    private get parentForm(): FormGroup | undefined
    {
        return this.controlContainer?.control as FormGroup || undefined;
    }

    get refresh(): Observable<void> { return this.refreshSubject.asObservable(); }

    get invalid(): boolean { return this.mailingAddressForm.invalid; }
    get hasAtLeastOneError(): boolean { return !!this.mailingAddressForm.errors["atLeastOne"]; }
    get hasNoPrimaryError(): boolean { return !!this.mailingAddressForm.errors["noPrimary"]; }

    ngOnDestroy(): void
    {
        this.parentForm?.removeControl("mailingAddressList");

        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.parentForm?.addControl("mailingAddressList", this.mailingAddressForm);

        if (this.mailingAddresses?.length === 1)
        {
            (this.mailingAddresses[0] as ExpandableMailingAddress).ExpandedDetail = true;
        }
    }

    // /**
    //  * @deprecated
    //  */
    // cancelClicked(): void
    // {
    //     const mailingAddresses = this.$mailingAddressService.getOriginalMailingAddresses();
    //     this.close.emit(<EventArgs<MailingAddressViewModel[]>>{ value: mailingAddresses });
    // }

    // /**
    //  * @deprecated
    //  */
    // doneClicked(): void
    // {
    //     // this.mailingAddressForm.$setSubmitted();
    //     const mailingAddressesToValidate = this.mailingAddresses.filter(addr => addr.RowState !== DataRowState.Deleted);
    //     if (this.mailingAddressForm.invalid
    //         || !this.$mailingAddressService.validateAddresses(mailingAddressesToValidate))
    //     {
    //         return;
    //     }

    //     const mailingAddresses = this.$mailingAddressService.confirmChanges(this.mailingAddresses);
    //     this.close.emit(<EventArgs<MailingAddressViewModel[]>>{ value: mailingAddresses });
    // }

    getAddressUsages(address: MailingAddressViewModel): string | undefined
    {
        return this.usages?.find(x => x.Id === address.Id)?.Value;
    }

    isDeleted(address: MailingAddressViewModel): boolean
    {
        return address.RowState === DataRowState.Deleted;
    }

    onEdit(address: ExpandableMailingAddress): void
    {
        if (!address)
        {
            throw new Error("MailingAddress is required for onEdit");
        }

        for (const mailingAddress of this.mailingAddresses)
        {
            mailingAddress.ExpandedDetail = address.Id === mailingAddress.Id;
        }
    }

    onPrimaryChanged(address: MailingAddressViewModel): void
    {
        if (!address)
        {
            throw new Error("MailingAddress is required for onPrimaryChanged");
        }

        const mailingAddresses = this.mailingAddresses
            .filter(x => x.RowState !== DataRowState.Deleted);
        for (const mailingAddress of mailingAddresses)
        {
            mailingAddress.IsPrimary = mailingAddress.Id === address.Id;
            if (mailingAddress.RowState !== DataRowState.Added)
            {
                mailingAddress.RowState = DataRowState.Modified;
            }
        }
        this.refreshSubject.next();
    }

    onForwardingChanged(address: MailingAddressViewModel): void
    {
        const mailingAddresses = this.mailingAddresses
            .filter(x => x.RowState !== DataRowState.Deleted);
        for (const mailingAddress of mailingAddresses)
        {
            mailingAddress.IsForwarding = mailingAddress.Id === address.Id;
            if (mailingAddress.RowState !== DataRowState.Added)
            {
                mailingAddress.RowState = DataRowState.Modified;
            }
        }

        this.refreshSubject.next();
    }

    onCertify(address: MailingAddressViewModel): void
    {
        if (!address.IsCertified)
        {
            this.$contactService.certifyMailingAddress(JSON.stringify(address))
                .then((result: MailingAddressViewModel) =>
                {
                    this.updateAddress(result);
                });
        }
        else
        {
            address.IsCertified = false;
            address.CertificationResults = "";
            this.updateAddress(address);
        }
    }

    private updateAddress(retAddress: ExpandableMailingAddress): void
    {
        const addressToUpdate = this.mailingAddresses.find(a => a.Id === retAddress.Id);
        // TODO - what happens if retAddress isn't in our list?

        if (retAddress.IsCertified)
        {
            addressToUpdate.Line1 = retAddress.Line1;
            addressToUpdate.Line2 = retAddress.Line2;
            addressToUpdate.City = retAddress.City;
            addressToUpdate.State = retAddress.State;
            addressToUpdate.PostalCode = retAddress.PostalCode;
            addressToUpdate.Country = retAddress.Country;
        }
        addressToUpdate.IsCertified = retAddress.IsCertified;
        addressToUpdate.CertificationResults = retAddress.CertificationResults;

        this.refreshSubject.next();
    }

    onDelete(address: MailingAddressViewModel): void
    {
        if (!address)
        {
            return;
        }

        if (this.getAddressUsages(address))
        {
            console.warn(`Cannot delete address while it is in use.`);
            return;
        }

        if (address.RowState === DataRowState.Added)
        {
            this.mailingAddresses.splice(this.mailingAddresses.indexOf(address, 0), 1);
        }
        else
        {
            address.RowState = DataRowState.Deleted;
        }

        this.refreshSubject.next();
    }
}
